/**
 * Zod schema for the Refer a Friend form, mirroring the backend's `SubmitReferralSchema`
 * (`api/src/refer-a-friend/refer-a-friend.model.ts`) so most invalid submissions never reach
 * the network. Messages are i18n keys — resolved via `t()` by `ReferAFriendModal`, not this
 * module. `referAFriend.validation.*` keys (in the `members` namespace, same convention as
 * `payments.validation.*`) are that component's to add; this file only needs the identifiers
 * to stay stable.
 */
import { z } from 'zod';

export const VALIDATION_KEYS = {
  referrerNameRequired: 'referAFriend.validation.referrerNameRequired',
  referrerNameTooLong: 'referAFriend.validation.referrerNameTooLong',
  referrerEmailRequired: 'referAFriend.validation.referrerEmailRequired',
  referrerEmailInvalid: 'referAFriend.validation.referrerEmailInvalid',
  recipientNameRequired: 'referAFriend.validation.recipientNameRequired',
  recipientNameTooLong: 'referAFriend.validation.recipientNameTooLong',
  recipientEmailRequired: 'referAFriend.validation.recipientEmailRequired',
  recipientEmailInvalid: 'referAFriend.validation.recipientEmailInvalid',
  recipientsMin: 'referAFriend.validation.recipientsMin',
  recipientsMax: 'referAFriend.validation.recipientsMax',
  recipientsDuplicateEmail: 'referAFriend.validation.recipientsDuplicateEmail',
} as const;

// Defined here rather than imported from the backend repo — the two schemas are mirrors,
// not a shared dependency.
const MAX_REFERRAL_RECIPIENTS = 5;

const isValidEmail = (email: string) => z.email().safeParse(email).success;

const ReferralRecipientSchema = z
  .object({ name: z.string().trim(), email: z.string().trim() })
  .superRefine((recipient, ctx) => {
    if (recipient.name === '') {
      ctx.addIssue({ code: 'custom', path: ['name'], message: VALIDATION_KEYS.recipientNameRequired });
    } else if (recipient.name.length > 255) {
      ctx.addIssue({ code: 'custom', path: ['name'], message: VALIDATION_KEYS.recipientNameTooLong });
    }

    if (recipient.email === '') {
      ctx.addIssue({ code: 'custom', path: ['email'], message: VALIDATION_KEYS.recipientEmailRequired });
    } else if (!isValidEmail(recipient.email)) {
      ctx.addIssue({ code: 'custom', path: ['email'], message: VALIDATION_KEYS.recipientEmailInvalid });
    }
  });

export const SubmitReferralSchema = z
  .object({
    referrerName: z.string().trim(),
    referrerEmail: z.string().trim(),
    recipients: z.array(ReferralRecipientSchema),
  })
  .superRefine((data, ctx) => {
    if (data.referrerName === '') {
      ctx.addIssue({ code: 'custom', path: ['referrerName'], message: VALIDATION_KEYS.referrerNameRequired });
    } else if (data.referrerName.length > 255) {
      ctx.addIssue({ code: 'custom', path: ['referrerName'], message: VALIDATION_KEYS.referrerNameTooLong });
    }

    if (data.referrerEmail === '') {
      ctx.addIssue({ code: 'custom', path: ['referrerEmail'], message: VALIDATION_KEYS.referrerEmailRequired });
    } else if (!isValidEmail(data.referrerEmail)) {
      ctx.addIssue({ code: 'custom', path: ['referrerEmail'], message: VALIDATION_KEYS.referrerEmailInvalid });
    }

    if (data.recipients.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['recipients'], message: VALIDATION_KEYS.recipientsMin });
    } else if (data.recipients.length > MAX_REFERRAL_RECIPIENTS) {
      ctx.addIssue({ code: 'custom', path: ['recipients'], message: VALIDATION_KEYS.recipientsMax });
    }

    // Case-insensitive: same rule the backend applies, since addresses are compared that way.
    // Attached to the array as a whole, not a recipient index — the modal owns per-row rendering.
    // Empty emails are excluded: each blank one already gets its own row-level "required" error,
    // and two blank rows are not "the same email address" as each other.
    const emails = data.recipients.map((recipient) => recipient.email.toLowerCase()).filter((email) => email !== '');
    if (new Set(emails).size !== emails.length) {
      ctx.addIssue({ code: 'custom', path: ['recipients'], message: VALIDATION_KEYS.recipientsDuplicateEmail });
    }
  });

export type SubmitReferralFormValues = z.infer<typeof SubmitReferralSchema>;
export type ReferralRecipientFormValues = z.infer<typeof ReferralRecipientSchema>;
